import { BrowserAuthErrorCodes, InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, msalInstance } from './msalConfig';
import { setTokenResolver, setUnauthorizedHandler } from '../../errors/errorHandling';

// Silent access-token renewal. The Microsoft access token lives ~60–90 min, but
// MSAL keeps a long-lived refresh token in localStorage, so a fresh access token
// can be fetched in the background without the user ever seeing a login screen.
// Only when that refresh token itself is dead (long inactivity, revoked session)
// is a real sign-in unavoidable.

let inFlight = null;
let inFlightForced = false;
let redirecting = false;
// Set once the refresh token is dead. Further silent attempts would only time out
// again, so background callers stop trying until the user signs in again.
let sessionExpired = false;
let lastIssuedToken = null;
// Every token this module handed out this session. Never trimmed: Redux keeps
// holding the first one, and requests carrying it must still be recognised and
// upgraded hours later. One short string per hour is negligible.
const issuedTokens = new Set();
let listener = null;

function rememberToken(token) {
  lastIssuedToken = token;
  issuedTokens.add(token);
}

// AdminHome subscribes to learn each renewed token's expiry (to schedule the
// next renewal). The token string itself is NOT pushed into Redux: dozens of
// effects refetch when `state.admin.token` changes, which would flash loaders
// every hour. Instead the fetch layer swaps in the newest token (see below).
export function isSessionExpired() {
  return sessionExpired;
}

export function onTokenRefreshed(fn) {
  listener = fn;
  return () => {
    if (listener === fn) listener = null;
  };
}

function currentAccount() {
  return msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
}

// SPA refresh tokens live 24 h. After that MSAL falls back to a hidden iframe,
// which times out when the browser blocks Microsoft's third-party cookies. That
// timeout means the same thing as InteractionRequiredAuthError: only a real
// sign-in can get a new token.
function needsInteraction(error) {
  return (
    error instanceof InteractionRequiredAuthError ||
    error?.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout
  );
}

// Redirect (not popup, which browsers block outside a click), once.
function redirectToSignIn(account) {
  if (redirecting) return;
  redirecting = true;
  msalInstance
    .acquireTokenRedirect({ scopes: loginRequest.scopes, account })
    .catch((redirectError) => {
      redirecting = false;
      console.warn('Sign-in redirect failed', redirectError);
    });
}

// Resolves to { accessToken, expiresOn } or null. Concurrent callers share one
// request, so ten parallel 401s trigger a single renewal.
//
// `redirectOnFailure` — when the refresh token itself is dead, send the user to
// Microsoft sign-in. Only the page-load call sets it: there is nothing on screen
// to lose yet. Background renewals must never navigate away from a half-filled
// form; a request that then fails just shows the usual "session expired" toast.
export function acquireTokenSilently({ forceRefresh = false, redirectOnFailure = false } = {}) {
  if (inFlight) {
    // A non-forced call may hand back the very cached token the server just
    // rejected, so a forced caller waits for it and then forces its own.
    if (forceRefresh && !inFlightForced) {
      return inFlight.then(() => acquireTokenSilently({ forceRefresh, redirectOnFailure }));
    }
    return inFlight;
  }
  const account = currentAccount();
  if (!account) return Promise.resolve(null);

  if (sessionExpired) {
    if (redirectOnFailure) redirectToSignIn(account);
    return Promise.resolve(null);
  }

  const request = { scopes: loginRequest.scopes, account, forceRefresh };
  inFlightForced = forceRefresh;
  inFlight = msalInstance
    .acquireTokenSilent(request)
    .then((response) => {
      const result = {
        accessToken: response.accessToken,
        expiresOn: response.expiresOn ? response.expiresOn.getTime() : null,
      };
      rememberToken(result.accessToken);
      if (listener) listener(result);
      return result;
    })
    .catch((error) => {
      if (needsInteraction(error)) {
        // The refresh token is gone — only an interactive sign-in can fix it.
        sessionExpired = true;
        if (redirectOnFailure) redirectToSignIn(account);
      } else {
        // Network blips etc. — keep the current session; the next attempt retries.
        console.warn('Silent token renewal failed', error);
      }
      return null;
    })
    .finally(() => {
      inFlight = null;
      inFlightForced = false;
    });
  return inFlight;
}

// Every outgoing request that carries one of our older tokens (typically the
// one still sitting in Redux) is sent with the newest token instead.
setTokenResolver((token) =>
  issuedTokens.has(token) && lastIssuedToken ? lastIssuedToken : token
);

// Called by the global fetch wrapper when a request comes back unauthenticated.
// Only tokens this module issued are renewed — requests carrying some other
// bearer (e.g. an onboarding link token) are left alone.
setUnauthorizedHandler(async (failedToken) => {
  if (!issuedTokens.has(failedToken)) return null;
  // A parallel request already renewed it — just retry with the newer token.
  if (failedToken !== lastIssuedToken) return lastIssuedToken;
  const result = await acquireTokenSilently({ forceRefresh: true });
  return result?.accessToken || null;
});
